import { useState, useRef, useCallback, useEffect } from 'react';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export type NotifTipo = 'fila_adicionada' | 'now_playing' | 'pedido_iniciado';

export interface NotifItem {
  id:        string;
  tipo:      NotifTipo;
  titulo:    string;
  thumbnail: string;
  /** Duração em ms antes de avançar para a próxima notificação */
  duracao:   number;
  clienteNome?:       string | null;
  clienteAvatarUrl?:  string | null;
}

export interface NotifAtiva extends NotifItem {
  /** true durante a animação de saída (antes de remover do DOM) */
  saindo: boolean;
}

// ── Constantes ────────────────────────────────────────────────────────────────

/** Tempo em ms da animação de saída (deve bater com notifOut no tailwind.config.js) */
const SAIDA_MS = 300;

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fila FIFO de notificações para a TV do Player.
 *
 * Garante que:
 * - Apenas uma notificação é exibida por vez
 * - Múltiplos pedidos Jukebox são enfileirados e exibidos sequencialmente
 * - A animação de saída é executada antes de avançar para a próxima
 *
 * Uso:
 * ```ts
 * const { notifAtiva, enfileirar, limpar } = useNotifQueue();
 * enfileirar({ tipo: 'fila_adicionada', titulo: '...', thumbnail: '...', duracao: 5000 });
 * ```
 */
export function useNotifQueue() {
  const filaRef   = useRef<Omit<NotifItem, 'id'>[]>([]);
  const timerRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contadorRef = useRef(0);

  const [notifAtiva, setNotifAtiva] = useState<NotifAtiva | null>(null);
  const ativaRef = useRef<NotifAtiva | null>(null);

  // Mantém ativaRef sincronizado para uso em closures
  useEffect(() => { ativaRef.current = notifAtiva; }, [notifAtiva]);

  /** Inicia a animação de saída e depois remove a notificação atual */
  const iniciarSaida = useCallback((callback?: () => void) => {
    setNotifAtiva(prev => prev ? { ...prev, saindo: true } : null);
    timerRef.current = setTimeout(() => {
      setNotifAtiva(null);
      callback?.();
    }, SAIDA_MS);
  }, []);

  /** Exibe o próximo item da fila, se houver */
  const avancarFila = useCallback(() => {
    const proximo = filaRef.current.shift();
    if (!proximo) return;

    contadorRef.current += 1;
    const nova: NotifAtiva = {
      ...proximo,
      id:     String(contadorRef.current),
      saindo: false,
    };
    setNotifAtiva(nova);

    timerRef.current = setTimeout(() => {
      iniciarSaida(avancarFila);
    }, proximo.duracao);
  }, [iniciarSaida]);

  /**
   * Enfileira uma nova notificação.
   *
   * - Se nada estiver exibindo, mostra imediatamente.
   * - Se já houver uma ativa, adiciona ao final da fila (FIFO).
   */
  const enfileirar = useCallback((item: Omit<NotifItem, 'id'>) => {
    if (!ativaRef.current) {
      // Exibe imediatamente
      contadorRef.current += 1;
      const nova: NotifAtiva = { ...item, id: String(contadorRef.current), saindo: false };
      setNotifAtiva(nova);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        iniciarSaida(avancarFila);
      }, item.duracao);
    } else {
      filaRef.current.push(item);
    }
  }, [iniciarSaida, avancarFila]);

  /**
   * Limpa imediatamente a notificação ativa e a fila pendente.
   * Use quando um evento de maior prioridade (ex: PedidoPopup) tomar a tela.
   */
  const limpar = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    filaRef.current = [];
    setNotifAtiva(null);
  }, []);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return { notifAtiva, enfileirar, limpar };
}
